import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useFetchDesiredCapabilitiesForOrganisation } from "../hooks/UnitMissionCapabilityHooks.tsx";
import { UnitMissionCapabilityForUnitAndMissionDTO } from "../types/unitMissionCapabilityForUnitAndMissionDTO.ts";
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from "react";
import { StationDTO } from "../types/stationDTO";
import { useFetchStations } from "../hooks/StationHooks";

type UnitMissionCapabilityArgs = {
    selectedStationId: string;
    selectedStationIdChanged: (selectedStationId: string) => void;
};



const UnitMissionCapabilityList = ({ selectedStationId, selectedStationIdChanged }: UnitMissionCapabilityArgs ) => {
  const { t } = useTranslation();
  const { organisationId } = useParams<{ organisationId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  const nav = useNavigate();
  const { data, isPending, isError } = useFetchDesiredCapabilitiesForOrganisation(organisationId);
  const [searchTerm, setSearchTerm] = useState('');
    const [stations, setStations] = useState<StationDTO[]>([]);
    const { data: stationsData, isLoading: stationsIsLoading, isError: stationsIsError } = useFetchStations(organisationId);
  

    useEffect(() => {
        if (stationsData) {
            setStations(stationsData);

            if (selectedStationId != "all") {
                const stationExists = stationsData.some(station => station.id === selectedStationId);
                if (!stationExists) {
                    selectedStationIdChanged("all"); // Reset to "all" if the selected station is not found
                }
            }
        }
    }, [stationsData]);

    useEffect(() => {
        if (isError || stationsIsError) { nav('/login') }

    }, [isError, stationsIsError]);
  
  if (isPending || stationsIsLoading) return <h2>Hold on</h2>;

  // group data by unit name to make it more clear to the user
  const groupByUnitName = (data: UnitMissionCapabilityForUnitAndMissionDTO[]) => {
      
      return data.reduce((acc, item) => {
      if (!acc[item.unit.name]) {
        acc[item.unit.name] = [];
      }
      acc[item.unit.name].push(item);
      return acc;
    }, {} as Record<string, UnitMissionCapabilityForUnitAndMissionDTO[]>);
  };

    const filteredData = groupByUnitName(data?.filter(umc => {
        const matchesSearch =
            umc.unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            umc.missionCode?.codedValue.toLowerCase().includes(searchTerm.toLowerCase()) ||
            umc.missionCode?.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
            umc.desiredCapabilities?.some(capability => capability.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            umc.requiredGroups?.some(group => group.name.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStation = selectedStationId === "all" || umc.unit.station.id === selectedStationId;

        return matchesSearch && matchesStation;

            
    }
  ) ?? []);

  return (
    <div>
      <div className="row mb-2 mt-2">
        <h5 className="subtitle text-left">
          {t('unitMissionCapability.title')}
        </h5>
      </div>

      <Link className="btn btn-primary mt-2 mb-3" to={`/organisation/${organisationId}/unitmissioncapability/add`}>
        {t('unitMissionCapability.add')}
      </Link>

          <div className="row mb-3">
              <div className="col-md-4">
                  <input
                      type="text"
                      className="form-control"
                      placeholder={t('common.search')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                  />
              </div>
              <div className="col-md-4">
                  <select
                      className="form-select"
                      value={selectedStationId}
                      onChange={(e) => {
                          selectedStationIdChanged(e.target.value);
                      }}
                  >
                      <option value="all">{t('common.fromAllStations')}</option>
                      {stations?.map((station) => (
                          <option key={station.id} value={station.id}>
                              {station.name}
                          </option>
                      ))}
                  </select>
              </div>
          </div>

      {Object.keys(filteredData).map(unitName => (
        <div key={unitName}>
          <p className="subtitleSmall ml-3 mb-1">{unitName}</p>
              <table className="table table-hover table-striped"
                  style={
                      {
                          "--bs-table-hover-bg": "#C9DDED"
                      } as React.CSSProperties
                  }
              >
            <thead>
              <tr>
                <th>{t("common.missionCode") }</th>
                <th>{t("unitMissionCapability.desiredCapabilities")}</th>                          
                <th>{t("unitMissionCapability.requiredGroups")}</th>                          
              </tr>
            </thead>
            <tbody>
              {filteredData[unitName].map(umc => (
                  <tr key={umc.missionCode?.id ?? '00000000-0000-0000-0000-000000000000'} style={{ cursor: 'pointer' }} onClick={() => nav(`/organisation/${organisationId}/unitmissioncapability?unitId=${umc.unit.id}&missionCodeId=${umc.missionCode?.id ?? '00000000-0000-0000-0000-000000000000'}`)}>
                  <td style={{ width: '30%' }}>{umc.missionCode?.codedValue ?? 'Oletus'} {umc.missionCode?.value ?? ''}</td>
                      <td style={{ width: '50%' }}>{umc.desiredCapabilities.filter(c => c.count !== 0).map(capability => `${capability.name} x ${capability.count}`).join(', ')}</td>                      
                      <td style={{ width: '20%' }}>{umc.requiredGroups.map(group => group.name ).join(', ')}</td>                      
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};


export default UnitMissionCapabilityList;